import { RefObject, useEffect, useRef } from 'react';

interface UseClickOutsideOptions {
	shouldIgnore?: (event: MouseEvent) => boolean;
}

/**
 * Calls `onOutside` when a mousedown lands outside the referenced element. The callback is held
 * in a ref so the listener binds once and never re-subscribes on re-render.
 */
export const useClickOutside = <T extends HTMLElement>(
	ref: RefObject<T | null>,
	onOutside: () => void,
	options: UseClickOutsideOptions = {},
) => {
	const callbackRef = useRef(onOutside);
	const optionsRef = useRef(options);
	callbackRef.current = onOutside;
	optionsRef.current = options;

	useEffect(() => {
		const handleMouseDown = (event: MouseEvent) => {
			const target = event.target as Node;
			if (optionsRef.current.shouldIgnore?.(event)) return;

			if (ref.current && !ref.current.contains(target)) {
				callbackRef.current();
			}
		};

		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, [ref]);
};
